// ─── components/UI/Menu.jsx ─────────────────────────────────────────────────
// Thin styled layer over Radix DropdownMenu (shadcn's pattern, hand-copied:
// the shadcn CLI assumes TS path aliases this CRA app doesn't have).
//
// Radix brings the behavior the hand-rolled header menus lacked: arrow keys,
// Home/End and type-to-jump between items, focus returned to the trigger on
// close, Escape and outside-click dismissal, and collision-aware placement.
// Its `data-state` / `--radix-dropdown-menu-content-transform-origin` drive
// one shared entrance (index.css, .uf-menu): the panel grows from the
// trigger's corner and its rows arrive a beat apart.
//
// The panel is the site's double-bezel: a paper tray with a hairline ring
// holding a white core, radii 24 → 18px.
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import * as DM from '@radix-ui/react-dropdown-menu';
import { BadgeTally } from './Badge';

export const Menu = DM.Root;
export const MenuTrigger = DM.Trigger;
export const MenuGroup = DM.Group;
export const MenuRadioGroup = DM.RadioGroup;

export function MenuContent({ children, className = '', coreClassName = '', align = 'end', sideOffset = 10, ...props }) {
  return (
    <DM.Portal>
      <DM.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        className={`uf-menu z-[60] rounded-3xl bg-press-paper p-1.5 font-sans shadow-[0_0_0_1px_rgba(18,22,28,0.06)] outline-none ${className}`}
        {...props}
      >
        <div className={`uf-menu-core max-h-[calc(var(--radix-dropdown-menu-content-available-height)-12px)] overflow-y-auto rounded-[18px] bg-press-card p-1.5 shadow-[0_1px_2px_rgba(18,22,28,0.05),0_24px_48px_-24px_rgba(18,22,28,0.35)] ${coreClassName}`}>
          {children}
        </div>
      </DM.Content>
    </DM.Portal>
  );
}
MenuContent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  coreClassName: PropTypes.string,
  align: PropTypes.oneOf(['start', 'center', 'end']),
  sideOffset: PropTypes.number,
};

const itemBase = 'relative flex w-full cursor-pointer select-none items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[14px] font-medium text-dark-primary no-underline outline-none transition-colors duration-200 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-press-paper';

// `to` renders a router Link, otherwise a button-like item using onSelect.
export function MenuItem({ to, icon, children, badge, tone, className = '', onSelect, ...props }) {
  // `!` because itemBase already sets a text colour and a highlight fill:
  // which of two same-layer utilities wins is decided by stylesheet order,
  // not by the order they appear here, so a destructive item needs to say so.
  const toneClass = tone === 'quiet' ? 'text-ink-soft'
    : tone === 'danger' ? '!text-error data-[highlighted]:!bg-error/10'
      : '';
  const content = (
    <>
      {icon && <span aria-hidden="true" className="flex flex-none text-ink-soft">{icon}</span>}
      <span className="min-w-0 flex-1">{children}</span>
      {badge > 0 && (
        <BadgeTally value={badge} />
      )}
    </>
  );
  if (to) {
    return (
      <DM.Item asChild onSelect={onSelect} {...props}>
        <Link to={to} className={`${itemBase} ${toneClass} ${className}`}>{content}</Link>
      </DM.Item>
    );
  }
  return (
    <DM.Item onSelect={onSelect} className={`${itemBase} ${toneClass} ${className}`} {...props}>
      {content}
    </DM.Item>
  );
}
MenuItem.propTypes = {
  to: PropTypes.string,
  icon: PropTypes.node,
  children: PropTypes.node,
  badge: PropTypes.number,
  tone: PropTypes.oneOf(['quiet', 'danger']),
  className: PropTypes.string,
  onSelect: PropTypes.func,
};

// A segmented control whose segments are real menuitemradio items, so they
// take part in the menu's arrow-key movement.
export function MenuSegment({ value, onValueChange, options, label }) {
  return (
    <DM.RadioGroup value={value} onValueChange={onValueChange} aria-label={label} className="flex rounded-full bg-dark-primary/[0.06] p-[3px]">
      {options.map((o) => (
        <DM.RadioItem
          key={o.value}
          value={o.value}
          className="flex-1 cursor-pointer rounded-full py-1.5 text-center text-[13px] font-semibold text-ink-soft outline-none transition-colors duration-200 data-[state=checked]:bg-surface data-[state=checked]:text-dark-primary data-[state=checked]:shadow-[0_1px_2px_rgba(26,34,48,0.1)] data-[highlighted]:ring-2 data-[highlighted]:ring-press-ink"
        >
          {o.label}
        </DM.RadioItem>
      ))}
    </DM.RadioGroup>
  );
}
MenuSegment.propTypes = {
  value: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  label: PropTypes.string,
};

export function MenuLabel({ children, className = '' }) {
  return <DM.Label className={`px-2.5 pb-1 pt-2.5 text-xs font-bold text-ink-soft ${className}`}>{children}</DM.Label>;
}
MenuLabel.propTypes = { children: PropTypes.node, className: PropTypes.string };

export function MenuSeparator() {
  return <DM.Separator className="mx-2 my-1.5 h-px bg-press-border" />;
}
